/**
 * CJdropshipping vs Dayone 子页面 — 页面常量（全量文案）
 * 路由：/cjdropshipping-vs-day-one-fulfillment
 *
 * 【注意】
 * - Dayone（DayOne）是一个 dropshipping 供应商平台，主打快速发货和精选供应商
 * - CJdropshipping 对比优势：全球仓储/质检/自定义包装/Print on Demand/更大产品目录
 * - 图片使用 CDN 路径（resources/egg/alternatives/），函数接受 cdnHost 参数
 */

/**
 * 【注意】图片 CDN 路径：${cdnHost}/resources/egg/alternatives/filename
 * 参照 odm/index.jsx 模式，接受 cdnHost 参数
 */
export function getDayoneConstants(cdnHost) {
  const img = (name) => `${cdnHost || ''}/resources/egg/alternatives/${name}`;
  return {
    // ========== VS_HERO: 子页 Banner ==========
    VS_HERO: {
      eyebrow: 'Dayone Alternative',
      title: 'CJdropshipping vs',
      titleHighlight: 'Day One Fulfillment',
      subtitle: 'Looking for a Better Dropshipping Day One Fulfillment Alternative?',
      desc: 'CJdropshipping brings product sourcing, fulfillment, warehousing, POD, custom packaging, and branding together to help you grow faster and scale smarter.',
      proofItems: ['Full supply chain support', 'Quality inspection', 'Custom branding'],
      ctaText: 'Try for free',
      ctaUrl: '/home',
      note: 'Built for dropshippers who want to scale with operational confidence.',
      heroImage: img('hero_banner4.jpg'),
      topChipText: 'Complete dropshipping supply chain',
      bottomCard: {
        label: 'Why CJdropshipping',
        title: 'More than curated products — a complete dropshipping operation',
        desc: 'Global sourcing, quality inspection, warehousing, custom packaging, and dedicated fulfillment — all in one platform.',
      },
    },

    // ========== SELLPOINTS: 卖点标签 ==========
    SELLPOINTS: [
      'Product Sourcing',
      'Global Fulfillment',
      'Quality Inspection',
      'Custom Packaging',
      'Print on Demand',
      'Global Warehouses',
      'Dedicated Agent Support',
    ],

    // ========== VS_TABLE: 三列对比表 ==========
    VS_TABLE: {
      kicker: 'Platform Comparison',
      title: '<strong>CJdropshipping</strong> vs Day One Fulfillment: Core Feature Comparison',
      desc: 'CJdropshipping offers a broader and more complete dropshipping supply chain than Dayone — with global sourcing, quality inspection, custom branding, POD, and warehousing support for serious sellers.',
      summaryCards: [
        {
          icon: '1',
          title: 'More than fulfillment',
          desc: 'Day One Fulfillment focuses on order fulfillment, while CJdropshipping supports the full dropshipping workflow from sourcing to fulfillment.',
        },
        {
          icon: '2',
          title: 'Built for growth',
          desc: 'CJdropshipping gives sellers sourcing, inspection, warehousing, shipping, POD, and packaging support in one platform.',
        },
        {
          icon: '3',
          title: 'Better for scaling',
          desc: 'CJdropshipping is stronger for sellers who want product sourcing, brand support, and fulfillment in one place.',
        },
      ],
      headerFeature: 'Feature',
      headerCompetitor: 'Dayone',
      headerCj: 'CJdropshipping',
      rows: [
        {
          feature: 'Product Positioning',
          competitor: 'A China-based sourcing and fulfillment service provider',
          cj: 'A complete dropshipping platform for sourcing, product import, fulfillment, warehousing, POD, custom packaging, and branding',
        },
        {
          feature: 'Main Advantage',
          competitor: 'Focuses mainly on sourcing, warehousing, packing, and shipping services',
          cj: 'Combines product sourcing, store connection, order fulfillment, global warehouses, POD, 3PL, and branding tools in one platform',
        },
        {
          feature: 'Dropshipping Workflow',
          competitor: 'More service-based fulfillment workflow',
          cj: 'More complete workflow from product discovery to order delivery',
        },
        {
          feature: 'Product Sourcing',
          competitor: 'Supports sourcing, but usually more service-driven',
          cj: 'Supports flexible product sourcing based on niche, target market, and seller needs',
        },
        {
          feature: 'Product Import',
          competitor: 'Not the main focus',
          cj: 'Supports product import into ecommerce stores, helping sellers move from sourcing to listing faster',
        },
        {
          feature: 'Store Integration',
          competitor: 'More focused on fulfillment service support rather than store operation workflows',
          cj: 'Better suited for Shopify, WooCommerce, TikTok Shop, eBay, and other ecommerce workflows',
        },
        {
          feature: 'Order Syncing',
          competitor: 'More manual or service-based depending on the fulfillment process',
          cj: 'Supports order synchronization for easier daily dropshipping management',
        },
        {
          feature: 'Fulfillment Support',
          competitor: 'Supports packing and shipping, but less platform-driven',
          cj: 'Supports order processing, packing, shipping, and logistics within a dropshipping-focused system',
        },
        {
          feature: 'Global Warehousing',
          competitor: 'Provides warehousing support, but with a more traditional fulfillment-service model',
          cj: 'Offers global warehouse and 3PL fulfillment support for scaling sellers',
        },
        {
          feature: '3PL Fulfillment',
          competitor: 'Offers 3PL-related support, but less connected to a full dropshipping platform workflow',
          cj: 'Allows sellers to store inventory and fulfill customer orders through CJ\'s fulfillment network',
        },
        {
          feature: 'POD Support',
          competitor: 'Not a core platform advantage',
          cj: 'Strong advantage: supports print-on-demand and personalized product opportunities',
        },
        {
          feature: 'Custom Packaging',
          competitor: 'Supports white label/private label services, but may be more service-based',
          cj: 'Supports custom packaging with brand elements to improve customer experience',
        },
        {
          feature: 'Branding Support',
          competitor: 'Provides branding-related services, but with less platform flexibility',
          cj: 'Stronger brand-building path through custom packaging, POD, white label, and customization options',
        },
        {
          feature: 'Scalability',
          competitor: 'Better for sellers who mainly need outsourced fulfillment support',
          cj: 'Better for sellers moving from product testing to long-term dropshipping and brand growth',
        },
        {
          feature: 'Best For',
          competitor: 'Sellers who mainly need a sourcing and fulfillment partner',
          cj: 'Sellers who want an all-in-one dropshipping platform with sourcing, fulfillment, customization, and branding',
        },
        {
          feature: 'Core Difference',
          competitor: 'Day One Fulfillment mainly supports the fulfillment and logistics side',
          cj: 'CJdropshipping helps sellers build the whole dropshipping business workflow',
        },
      ],
    },

    // ========== FIT: Who Should Choose CJdropshipping ==========
    FIT: {
      kicker: 'Seller Fit Guide',
      title: 'Who Should Choose <strong>CJdropshipping?</strong>',
      desc: 'CJdropshipping is a better fit for sellers who:',
      imageUrl: img('fit_who_should_choose.jpg'),
      badgeText: '✓ Best Fit for Growth Sellers',
      floatTitle: 'Supply chain-focused dropshippers',
      floatDesc: 'A stronger fit for sellers who want more than a curated product catalog — they want full operational and brand-building support.',
      items: [
        'Need reliable product sourcing',
        'Want fulfillment support for dropshipping orders',
        'Sell through Shopify, WooCommerce, TikTok Shop, or eBay',
        'Want to test or sell POD products',
        'Need custom packaging or branding services',
        'Want to improve shipping and customer experience',
        'Plan to move from marketplace reselling to brand-based ecommerce',
        'See dropshipping as a long-term business, not just short-term product testing',
      ],
      summaryTitle: 'Why CJdropshipping stands out',
      summaryDesc: 'CJdropshipping is ideal for sellers who want to go beyond simple product copying. It helps you build a stronger business foundation through sourcing, fulfillment, customization, and branding support.',
    },

    // ========== ADVANTAGES: 核心优势 ==========
    ADVANTAGES: {
      kicker: 'Competitive Advantages',
      title: 'How <strong>CJdropshipping</strong> beat the competition',
      desc: 'CJdropshipping is a full dropshipping supply chain partner. It helps sellers go beyond product listing with sourcing, fulfillment, shipping, branding, and dedicated support.',
      cards: [
        {
          num: '01',
          title: 'Full Dropshipping Supply Chain Support',
          desc: 'CJdropshipping covers more than listing. It supports product sourcing, procurement, warehousing, fulfillment, shipping, and after-sales workflows.',
          imageUrl: img('adv_supply_chain.jpg'),
        },
        {
          num: '02',
          title: 'Reliable Product Sourcing',
          desc: 'Sellers can submit sourcing requests and find products through CJdropshipping\'s supplier resources, making it easier to discover better products and improve margins.',
          imageUrl: img('adv_sourcing.jpg'),
        },
        {
          num: '03',
          title: 'Quality Inspection Before Shipping',
          desc: 'CJdropshipping helps inspect products and packages before shipment, reducing the risk of defects, complaints, refunds, and negative reviews.',
          imageUrl: img('adv_quality_inspection.jpg'),
        },
        {
          num: '04',
          title: 'Global Warehousing',
          desc: 'With warehouse support in different markets, CJdropshipping helps sellers store inventory closer to customers and improve fulfillment efficiency.',
          imageUrl: img('adv_warehousing.jpg'),
        },
        {
          num: '05',
          title: 'End-to-End Order Fulfillment',
          desc: 'CJdropshipping can handle order processing, picking, packing, shipping, tracking, and delivery support, helping sellers reduce manual work.',
          imageUrl: img('adv_fulfillment.jpg'),
        },
        {
          num: '06',
          title: 'Global Shipping Solutions',
          desc: 'CJdropshipping provides global logistics options to help sellers deliver products to customers more efficiently and reliably.',
          imageUrl: img('adv_shipping.jpg'),
        },
        {
          num: '07',
          title: 'Custom Packaging and Branding',
          desc: 'Sellers can create branded packaging, inserts, labels, and unboxing experiences, making it easier to build a real ecommerce brand.',
          imageUrl: img('adv_custom_packaging.jpg'),
        },
        {
          num: '08',
          title: 'Dedicated Agent Support',
          desc: 'CJdropshipping provides service support to help sellers with sourcing, fulfillment, logistics, and operational issues as their business grows.',
          imageUrl: img('adv_dedicated_support.jpg'),
        },
      ],
      footerCta: {
        text: 'CJdropshipping gives sellers more than a curated product catalog. It provides the full supply chain infrastructure — inspection, warehousing, branding, and dedicated support — to help serious dropshippers scale with confidence.',
        ctaText: 'GET STARTED',
        ctaUrl: '/home',
      },
    },

    // ========== TESTIMONIALS: 买家评价 ==========
    TESTIMONIALS: {
      kicker: 'Real Seller Stories',
      title: 'Why Growing Sellers <strong>Stay with CJdropshipping</strong>',
      desc: 'Hear how ecommerce sellers use CJdropshipping to streamline sourcing, fulfillment, branding, and shipping while building stronger long-term businesses.',
      rows: [
        [
          {
            avatarUrl: img('32.jpeg'),
            name: 'Emily Carter',
            role: 'Shopify Store Owner',
            country: 'United States',
            text: 'CJdropshipping helped me simplify my fulfillment process. Instead of manually coordinating with multiple suppliers, I can manage sourcing, orders, and shipping in one place. It saves me a lot of time every week.',
          },
          {
            avatarUrl: img('12.jpeg'),
            name: 'Daniel Hughes',
            role: 'Ecommerce Entrepreneur',
            country: 'United Kingdom',
            text: 'As my store started growing, I needed more than just a product listing tool. CJdropshipping gave me access to sourcing, fulfillment, and packaging support, which made it easier to scale my business.',
          },
          {
            avatarUrl: img('47.jpeg'),
            name: 'Sofia Martinez',
            role: 'Dropshipping Seller',
            country: 'Spain',
            text: 'One of the biggest advantages for me is product sourcing. When I cannot find the right product, CJdropshipping helps me look for alternatives and connect with supply options that fit my store.',
          },
        ],
        [
          {
            avatarUrl: img('15.jpeg'),
            name: 'Liam Walker',
            role: 'DTC Brand Founder',
            country: 'Australia',
            text: 'I wanted my store to feel more professional, and CJdropshipping\'s custom packaging and branding options helped a lot. It made my customer experience feel less generic.',
          },
          {
            avatarUrl: img('5.jpeg'),
            name: 'Chloe Anderson',
            role: 'Online Store Operator',
            country: 'Canada',
            text: 'Managing products, orders, and fulfillment through one platform makes daily operations much easier. CJdropshipping is especially helpful if you want to reduce repetitive manual work.',
          },
          {
            avatarUrl: img('60.jpeg'),
            name: 'Marco Rossi',
            role: 'Ecommerce Seller',
            country: 'Italy',
            text: 'CJdropshipping feels like a better fit for sellers who want to build a serious dropshipping business. It is not only about listing products, but also about improving sourcing, shipping, and the overall customer experience.',
          },
        ],
      ],
    },

    // ========== VS_FAQ: 子页专属 FAQ ==========
    VS_FAQ: {
      kicker: 'FAQ',
      title: 'Dayone vs <strong>CJdropshipping</strong> FAQ',
      intro:
        'Get quick answers about how CJdropshipping compares with Dayone for dropshipping — covering sourcing, quality inspection, fulfillment, branding, and building a long-term business.',
      sideCard: {
        label: 'Quick Decision Guide',
        title: 'Need more than a curated product catalog?',
        desc: 'Dayone offers curated supplier products for dropshipping. CJdropshipping gives sellers a more complete supply chain with global sourcing, quality inspection, warehousing, fulfillment, and branding support.',
        miniItems: [
          { text: 'Broader product sourcing with custom requests' },
          { text: 'Quality inspection before shipping' },
          { text: 'Custom packaging and dedicated agent support' },
        ],
      },
      items: [
        {
          q: 'Is CJdropshipping a Day One Fulfillment alternative?',
          a: 'Yes. CJdropshipping can be a Day One Fulfillment alternative for sellers who need more than sourcing and fulfillment support. CJdropshipping offers a more complete dropshipping platform with product sourcing, product import, order fulfillment, global warehousing, POD, custom packaging, 3PL fulfillment, and branding support.',
        },
        {
          q: 'What is the main difference between CJdropshipping and Day One Fulfillment?',
          a: 'Day One Fulfillment is more like a sourcing and fulfillment partner, while CJdropshipping is a full dropshipping platform. CJdropshipping supports the complete workflow from product sourcing and store product import to order fulfillment, warehousing, POD, custom packaging, and brand growth.',
        },
        {
          q: 'Why do sellers look for a Day One Fulfillment alternative?',
          a: 'Sellers may look for a Day One Fulfillment alternative when they want a more platform-based workflow, easier product import, automated order fulfillment, POD options, custom packaging, global warehousing, or a more scalable dropshipping system.',
        },
        {
          q: 'Which platform is better for product sourcing?',
          a: 'Both platforms can support product sourcing, but CJdropshipping is better suited for sellers who want sourcing connected with product listing, order fulfillment, warehousing, and branding workflows.',
        },
        {
          q: 'Is Day One Fulfillment good for 3PL fulfillment?',
          a: 'Day One Fulfillment can be useful for sellers who need warehousing, packing, shipping, and fulfillment support. However, CJdropshipping offers a broader dropshipping-focused workflow that also includes product sourcing, product import, POD, custom packaging, and branding.',
        },
        {
          q: 'Which one is better for Shopify sellers?',
          a: 'CJdropshipping is usually a stronger choice for Shopify sellers who want an integrated workflow for sourcing, importing products, fulfilling orders, and building a branded store experience. Day One Fulfillment may be suitable for sellers who mainly need fulfillment service support.',
        },
        {
          q: 'Which one is better for beginners?',
          a: 'CJdropshipping is more beginner-friendly for many dropshipping sellers because it combines sourcing, product import, order fulfillment, and store workflow support in one platform. Day One Fulfillment may be better for sellers who already know what products they want to source or fulfill.',
        },
        {
          q: 'Who should choose CJdropshipping instead of Day One Fulfillment?',
          a: 'CJdropshipping is a better choice for sellers who want a complete dropshipping platform with sourcing, product import, order fulfillment, global warehousing, POD, custom packaging, 3PL fulfillment, and branding support.',
        },
        {
          q: 'Is CJdropshipping good for long-term ecommerce growth?',
          a: 'Yes. CJdropshipping is designed for sellers who want to move beyond basic fulfillment and build a more scalable ecommerce business with sourcing, fulfillment, customization, packaging, warehousing, and brand growth support.',
        },
      ],
      bottomText: 'Ready to go beyond a curated product catalog? Start with a free account and explore CJdropshipping\'s full supply chain capabilities.',
      bottomCtaText: 'Try CJdropshipping Free',
      bottomCtaUrl: '/home',
    },

    // ========== BOTTOM_CTA: 底部行动召唤 ==========
    BOTTOM_CTA: {
      kicker: 'Ready to Scale Smarter?',
      title: 'Ready to move from fulfillment support to a complete dropshipping platform?',
      desc: 'Start with CJdropshipping and build a more scalable ecommerce business.',
      ctaText: 'Upgrade from Day One Fulfillment to CJdropshipping',
      ctaUrl: '/home',
      bgImage: img('hero_banner.jpg'),
    },
  };
}
